<template>
  <BaseCenteredModal
    v-model:is-modal-open="localModalVisible"
    :title="title"
    :size="isInvoiceHistory ? 'medium' : 'large'"
    @close="close"
  >
    <template #body>
      <div v-if="loaded" class="flex w-full flex-col gap-4">
        <div
          v-if="run"
          class="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7"
        >
          <div
            v-for="counter in counters"
            :key="counter.label"
            class="rounded border border-gray-200 p-2 text-center dark:border-slate-600"
          >
            <div class="text-xs opacity-70">{{ counter.label }}</div>
            <div class="text-lg font-semibold">{{ counter.value }}</div>
          </div>
        </div>

        <div
          v-if="run?.error"
          class="rounded bg-red-100 p-2 text-sm text-red-800"
        >
          {{ run.error }}
        </div>

        <div v-if="totalLines" class="flex items-center gap-2">
          <div class="relative flex-1">
            <FontAwesomeIcon
              :icon="['fal', 'search']"
              class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs opacity-50"
            />
            <input
              v-model="search"
              type="search"
              class="w-full rounded border border-gray-300 py-1.5 pr-3 pl-8 text-sm dark:border-slate-600 dark:bg-slate-700"
              :placeholder="$t('invoicing.payment_sync_search_placeholder')"
            />
          </div>
          <span class="text-xs whitespace-nowrap opacity-60">
            {{ filteredLines.length }} / {{ totalLines }}
          </span>
        </div>

        <template v-if="filteredLines.length">
          <div class="overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead class="bg-secondary text-contrast-secondary">
                <tr>
                  <th v-if="!isInvoiceHistory" class="p-2">
                    {{ $t('invoicing.payment_sync_invoice_column') }}
                  </th>
                  <th v-else class="p-2">
                    {{ $t('invoicing.payment_sync_run_column') }}
                  </th>
                  <th class="p-2">
                    {{ $t('invoicing.payment_sync_status_change') }}
                  </th>
                  <th class="p-2 text-right">
                    {{ $t('invoicing.payment_sync_payments_added') }}
                  </th>
                  <th class="p-2 text-right">
                    {{ $t('invoicing.payment_sync_total_paid_column') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="line in paginatedLines" :key="line.id">
                  <tr
                    class="border-b align-top"
                    :class="{
                      'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700':
                        !isInvoiceHistory
                    }"
                    @click="openInvoice(line)"
                  >
                    <td v-if="!isInvoiceHistory" class="p-2">
                      <span class="font-medium">
                        {{ line.invoice_reference ?? line.invoice_id }}
                      </span>
                      <span class="block text-xs opacity-60">
                        {{ $t('invoicing.chift_reference') }}
                        {{ line.external_invoice_id }}
                      </span>
                    </td>
                    <td v-else class="p-2 text-xs whitespace-nowrap">
                      {{
                        $h.formatDate(
                          line.run?.started_at,
                          'D MMM YYYY HH:mm',
                          $i18n.locale.substring(0, 2)
                        )
                      }}
                    </td>
                    <td class="p-2">
                      <span :title="line.status_before">
                        {{ statusLabel(line.status_before) }}
                      </span>
                      <span class="px-1">&rarr;</span>
                      <span class="font-semibold" :title="line.status_after">
                        {{ statusLabel(line.status_after) }}
                      </span>
                    </td>
                    <td class="p-2 text-right">
                      {{ line.payments_created }}
                      <span
                        v-if="line.payments_updated"
                        class="text-xs opacity-60"
                      >
                        (+{{ line.payments_updated }})
                      </span>
                    </td>
                    <td class="p-2 text-right whitespace-nowrap">
                      {{ $h.formatCurrency(line.total_paid_before) }}
                      <span class="px-1">&rarr;</span>
                      <span class="font-semibold">
                        {{ $h.formatCurrency(line.total_paid_after) }}
                      </span>
                    </td>
                  </tr>
                  <tr
                    v-if="line.payments?.length"
                    class="border-b"
                    :class="{
                      'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700':
                        !isInvoiceHistory
                    }"
                    @click="openInvoice(line)"
                  >
                    <!-- Spans the row on purpose: listing the payments inside a cell
                       widened that column enough to push the table into a scrollbar. -->
                    <td colspan="4" class="px-2 pb-2">
                      <hr class="mb-2 border-gray-200 dark:border-slate-600" />
                      <ul class="flex flex-col gap-0.5 text-xs">
                        <li
                          v-for="(payment, index) in line.payments"
                          :key="`${line.id}-${index}`"
                          class="flex items-center gap-2"
                        >
                          <FontAwesomeIcon
                            :icon="[
                              'fas',
                              payment.action === 'created' ? 'plus' : 'rotate'
                            ]"
                            class="text-[10px]"
                            :class="
                              payment.action === 'created'
                                ? 'text-green-600'
                                : 'opacity-50'
                            "
                          />
                          <span class="font-medium">
                            {{ $h.formatCurrency(payment.amount) }}
                          </span>
                          <span class="opacity-60">
                            {{ $t('invoicing.chift_reference') }}
                            {{ payment.chift_id }}
                          </span>
                        </li>
                      </ul>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <PaginationComponent
            v-if="filteredLines.length > pageLength"
            :entries-length="filteredLines.length"
            :page-length="pageLength"
            :current-page="currentPage"
            @update:current-page="currentPage = $event"
          />
        </template>

        <div v-else class="text-center text-sm opacity-70">
          {{ emptyMessage }}
        </div>
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import PaginationComponent from '@c/PaginationComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapActions } from 'vuex'
import { nonZeroExtraCounters } from '@c/Partner/Chift/paymentSyncPartner'

export default {
  name: 'ChiftPaymentSyncRunModal',
  components: { BaseCenteredModal, PaginationComponent, FontAwesomeIcon },
  props: {
    /**
     * One run, listing every invoice it changed. Company settings use this.
     */
    runId: {
      type: String,
      default: null
    },
    /**
     * Everything the sync ever did to one invoice, across runs. The invoice page
     * uses this. Exclusive with runId.
     */
    invoiceId: {
      type: String,
      default: null
    }
  },
  emits: ['close'],
  data() {
    return {
      localModalVisible: false,
      run: null,
      lines: [],
      search: '',
      currentPage: 1,
      // A full run reports one line per changed invoice, and a first sync on a real
      // estate changes hundreds of them. Paginating client-side keeps the payload as
      // it is while the table stays readable.
      pageLength: 15
    }
  },
  computed: {
    isInvoiceHistory() {
      return !!this.invoiceId
    },
    loaded() {
      return this.isInvoiceHistory ? this.localModalVisible : !!this.run
    },
    title() {
      return this.isInvoiceHistory
        ? this.$t('invoicing.payment_sync_invoice_history')
        : this.$t('invoicing.payment_sync_run_detail')
    },
    emptyMessage() {
      if (this.search) {
        return this.$t('invoicing.payment_sync_no_match')
      }

      return this.isInvoiceHistory
        ? this.$t('invoicing.payment_sync_invoice_never_touched')
        : this.$t('invoicing.payment_sync_no_invoice_changed')
    },
    counters() {
      if (!this.run) {
        return []
      }

      return [
        {
          label: this.$t('invoicing.payment_sync_scanned'),
          value: this.run.scanned
        },
        {
          label: this.$t('invoicing.payment_sync_created'),
          value: this.run.created
        },
        {
          label: this.$t('invoicing.payment_sync_updated'),
          value: this.run.updated
        },
        {
          label: this.$t('invoicing.payment_sync_cancelled'),
          value: this.run.cancelled
        },
        {
          label: this.$t('invoicing.payment_sync_unmatched'),
          value: this.run.unmatched
        },
        {
          label: this.$t('invoicing.payment_sync_skipped_not_enabled'),
          value: this.run.skipped_not_enabled
        },
        {
          label: this.$t('invoicing.payment_sync_skipped_non_financial'),
          value: this.run.skipped_non_financial
        },
        ...nonZeroExtraCounters(this.run).map((counter) => ({
          label: this.$t(counter.key),
          value: counter.value
        }))
      ]
    },
    totalLines() {
      return this.lines.length
    },
    filteredLines() {
      const needle = this.search.trim().toLowerCase()

      if (!needle) {
        return this.lines
      }

      // Matches what is on screen plus the accounting references behind it, so
      // pasting a payment id from the accounting software finds its invoice.
      return this.lines.filter((line) =>
        [
          line.invoice_reference,
          line.invoice_id,
          line.external_invoice_id,
          ...(line.payments ?? []).map((payment) => payment.chift_id)
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(needle))
      )
    },
    paginatedLines() {
      const start = (this.currentPage - 1) * this.pageLength

      return this.filteredLines.slice(start, start + this.pageLength)
    }
  },
  watch: {
    search() {
      this.currentPage = 1
    },
    runId: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          if (!this.invoiceId) {
            this.localModalVisible = false
          }
          return
        }

        this.fetchRun(newValue)
      }
    },
    invoiceId: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          if (!this.runId) {
            this.localModalVisible = false
          }
          return
        }

        this.fetchInvoiceHistory(newValue)
      }
    }
  },
  methods: {
    ...mapActions({
      processGetPaymentSyncRunAction: 'company/getPaymentSyncRun',
      processFetchInvoicePaymentSyncHistoryAction:
        'invoicing/fetchInvoicePaymentSyncHistory'
    }),
    /**
     * Codes alone say nothing to a user reading a report: EST and PAI are internal.
     * The code is kept alongside the label because it is what the accounting side
     * and the logs show.
     */
    statusLabel(status) {
      if (!status) {
        return '—'
      }

      const key = `enums.invoicing.status.${status}`
      const label = this.$t(key)

      // Label only: the pair of codes doubled the column width and pushed the table
      // into a horizontal scrollbar. The raw code stays on the cell's title, which is
      // what matches the logs and the accounting side.
      return label === key ? status : label
    },
    reset() {
      this.search = ''
      this.currentPage = 1
    },
    fetchRun(runId) {
      this.processGetPaymentSyncRunAction({ id: runId })
        .then((response) => {
          this.run = response.run
          this.lines = response.run?.lines ?? []
          this.reset()
          this.localModalVisible = true
        })
        .catch(() => {
          // Opening a run is an explicit click: failing silently left the user
          // staring at a list that did nothing.
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          this.close()
        })
    },
    fetchInvoiceHistory(invoiceId) {
      this.processFetchInvoicePaymentSyncHistoryAction({
        invoice_id: invoiceId
      })
        .then((response) => {
          this.run = null
          this.lines = response.lines ?? []
          this.reset()
          this.localModalVisible = true
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          this.close()
        })
    },
    openInvoice(line) {
      // Already on the invoice in history mode, and the report is read from company
      // settings otherwise — navigating in place would lose it mid-read.
      if (this.isInvoiceHistory || !line.invoice_id) {
        return
      }

      const route = this.$router.resolve({
        name: 'invoice_show_route',
        params: { invoicing_id: line.invoice_id }
      })

      window.open(route.href, '_blank', 'noopener')
    },
    close() {
      this.localModalVisible = false
      this.run = null
      this.lines = []
      this.reset()
      this.$emit('close')
    }
  }
}
</script>
